import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PiCalendarBlankBold, PiCurrencyDollarBold, PiHandshakeBold, PiTrendUpBold } from 'react-icons/pi';
import { LoadingState, PageHeader } from '@v2/design-system';
import api from '@v2/lib/api';
import type { SalesCalendarDay, SalesCalendarResponse } from './types';
import SalesMonthCalendar from './SalesMonthCalendar';
import SalesDayDetailModal from './SalesDayDetailModal';

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v ?? 0);

function monthKey(d: Date) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function KpiBox({ label, value, sub, icon: Icon }: { label: string; value: string; sub?: string; icon: React.ElementType }) {
    return (
        <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-zinc-700 dark:bg-zinc-900">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-zinc-400">
                <Icon className="size-4" />
                {label}
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
            {sub && <p className="mt-1 text-xs text-gray-500">{sub}</p>}
        </div>
    );
}

export default function CommissionSalesCalendarPage() {
    const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [selectedDate, setSelectedDate] = useState<string | null>(null);

    const { data, isLoading, isError, error } = useQuery<SalesCalendarResponse>({
        queryKey: ['commissioning', 'sales-calendar', monthKey(month)],
        queryFn: () => api.get('/commissioning/sales-calendar', { params: { month: monthKey(month) } }).then((r) => r.data),
    });

    const days = data?.days ?? [];
    const meta = data?.meta;

    const maxAmount = useMemo(
        () => days.reduce((max, day) => Math.max(max, day.total_amount), 0),
        [days],
    );

    const selectedDay: SalesCalendarDay | null = useMemo(() => {
        if (!selectedDate) return null;
        return days.find((d) => d.date === selectedDate) ?? null;
    }, [days, selectedDate]);

    const handleDayClick = (day: SalesCalendarDay | null, date: string) => {
        setSelectedDate(date);
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Calendário de Vendas"
                subtitle="Volume comercializado por dia — deals ganhos no Ploomes (data de ganho)"
            />

            {isLoading && <LoadingState label="Carregando calendário…" />}
            {isError && (
                <p className="text-sm text-rose-600">
                    Erro ao carregar: {(error as Error)?.message ?? 'tente novamente'}
                </p>
            )}

            {meta && (
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    <KpiBox
                        icon={PiCurrencyDollarBold}
                        label="Volume do mês"
                        value={brl(meta.total_amount)}
                        sub={`${meta.total_deals} deal(s) ganho(s)`}
                    />
                    <KpiBox
                        icon={PiHandshakeBold}
                        label="Vendas no mês"
                        value={String(meta.total_deals)}
                        sub="Propostas ganhas com valor"
                    />
                    <KpiBox
                        icon={PiCalendarBlankBold}
                        label="Dias com venda"
                        value={String(meta.active_days)}
                        sub={`De ${new Date(meta.end).getDate()} dias no mês`}
                    />
                    <KpiBox
                        icon={PiTrendUpBold}
                        label="Média por dia ativo"
                        value={brl(meta.avg_per_active_day)}
                        sub="Volume / dias com venda"
                    />
                </div>
            )}

            {!isLoading && data && (
                <>
                    <SalesMonthCalendar
                        month={month}
                        days={days}
                        maxAmount={maxAmount}
                        onMonthChange={(d) => {
                            setMonth(d);
                            setSelectedDate(null);
                        }}
                        onDayClick={handleDayClick}
                        selectedDate={selectedDate}
                    />

                    <p className="text-xs text-gray-500">
                        Cada célula mostra o valor total comercializado no dia da ganho da proposta no Ploomes.
                        Clique em um dia para ver os deals. Dias mais escuros = maior volume relativo ao mês.
                    </p>
                </>
            )}

            <SalesDayDetailModal
                day={selectedDay}
                date={selectedDate}
                onClose={() => setSelectedDate(null)}
            />
        </div>
    );
}
